<template>
  <div mt-32px flex items-center justify-end>
    <span text-size-16px color="#000000e6">{{ selectLabel }}</span>
    <div class="worker-select__control">
      <el-select
        v-model="worker"
        :disabled="disabled || !!fixedWorker"
        ml-8px
        size="large"
        style="width: 160px"
        placeholder="请选择员工"
        :filterable="usesReservationCandidateApi"
        :remote="usesReservationRemoteSearch"
        :remote-method="usesReservationRemoteSearch ? handleReservationRemoteSearch : undefined"
        :loading="usesReservationRemoteSearch && reservationLoading"
      >
        <el-option
          v-for="item in fixedWorker ? [fixedWorker] : workerList"
          :key="item.value"
          :label="item.label"
          :value="item.value"
          :disabled="item.disabled"
        />
      </el-select>
      <span
        v-if="usesReservationRemoteSearch && reservationHasMore"
        class="worker-select__search-hint"
        data-test="reservation-worker-search-hint"
      >
        员工较多，请输入姓名或门店搜索
      </span>
    </div>
    <el-button
      v-if="secondaryButtonText && !isMergeTable"
      class="button button--secondary"
      data-test="worker-secondary-action"
      :disabled="disabled"
      @click="handleSecondaryClick"
    >
      {{ secondaryButtonText }}
    </el-button>
    <el-button
      v-if="!isMergeTable"
      class="button"
      :class="{ 'button--after-secondary': secondaryButtonText }"
      data-test="worker-primary-action"
      :disabled="disabled"
      @click="handleClick"
    >
      {{ buttonText }}
    </el-button>

    <el-popconfirm
      v-else
      title="确定将已选桌台的消费并入当前消费账单？"
      width="240px"
      cancel-button-text="暂不"
      @confirm="handleClick"
    >
      <template #reference>
        <el-button class="button" data-test="worker-primary-action" :disabled="disabled">
          {{ buttonText }}
        </el-button>
      </template>
    </el-popconfirm>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from "vue"
import type { ReservationEmployeeOption } from "@/api/reservation"
import { ElMessage } from "element-plus"
import { computed, onBeforeUnmount, ref } from "vue"
import { queryReservationEmployeeOptions } from "@/api/reservation"
import { getWorkerListByShopId } from "@/api/user/worker"
import { useUserStore } from "@/store/modules/user"
import { useViewScopeStoreHook } from "@/store/modules/viewScope"

const useUser = useUserStore()
const shopId = useUser.shopId
const userInfo = useUser.getUserInfo?.()
const viewScopeStore = useViewScopeStoreHook()

const emit = defineEmits(["update:worker", "secondary-click"])

const props = defineProps({
  fixedWorker: { type: Object as PropType<{ value: number, label: string, disabled?: boolean }>, default: undefined },
  selectLabel: {
    type: String,
    required: false,
    default: "操作员工：",
  },
  buttonText: {
    type: String,
    required: false,
    default: "确定",
  },
  secondaryButtonText: {
    type: String,
    required: false,
    default: "",
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  permissinList: {
    type: Array as PropType<string[]>,
    required: false,
    default: () => [],
  },
  isMergeTable: {
    type: Boolean,
  },
  selectionMode: {
    type: String as PropType<"default" | "reservation">,
    default: "default",
  },
})

const worker = ref()
const workerList = ref<
  {
    value: number
    label: string
    disabled: boolean
  }[]
>([])

const compatibilityFallback = ref(false)
const RESERVATION_EMPLOYEE_PAGE_SIZE = 100
const RESERVATION_EMPLOYEE_SEARCH_DELAY_MS = 250
const reservationHasMore = ref(false)
const reservationLoading = ref(false)
let reservationRequestSequence = 0
let reservationSearchTimer: ReturnType<typeof setTimeout> | undefined

type WorkerOption = {
  value: number
  label: string
  disabled: boolean
}

const usesReservationCandidateApi = computed(() => {
  const trustedBrandId = viewScopeStore.activeScope?.meta?.brandId
  return props.selectionMode === "reservation" && trustedBrandId != null && trustedBrandId > 0
})
const usesReservationRemoteSearch = computed(
  () => usesReservationCandidateApi.value && !compatibilityFallback.value,
)

function toWorkerOption(item: ReservationEmployeeOption, scope?: string) {
  const value = item.corpUserId
  const name = item.name?.trim()
  if (value == null || value <= 0 || !name || item.selectable === false) {
    return undefined
  }
  const shopNames = scope === "BRAND"
    ? [...new Set((item.boundShopNames ?? []).map(shopName => shopName?.trim()).filter(Boolean))]
    : []
  const label = shopNames.length > 0 ? `${name}（${shopNames.join("、")}）` : name
  return { value, label, disabled: false }
}

function mergeWorkerOptions(
  scope: string | undefined,
  ...groups: Array<Array<ReservationEmployeeOption | WorkerOption> | undefined>
) {
  const byCorpUserId = new Map<number, WorkerOption>()
  for (const item of groups.flatMap(group => group ?? [])) {
    const option = "value" in item ? item : toWorkerOption(item, scope)
    if (option) byCorpUserId.set(option.value, option)
  }
  return [...byCorpUserId.values()]
}

async function loadLegacyWorkers() {
  reservationHasMore.value = false
  const res = await getWorkerListByShopId(shopId, props.permissinList)
  workerList.value = res.data.flatMap((item) => {
    const value = item.corpUserId
    const label = item.name?.trim()
    return value != null && value > 0 && label
      ? [{ value, label, disabled: false }]
      : []
  })
  worker.value = workerList.value.find(item => item.value === userInfo?.corpUserId)?.value
}

async function loadReservationWorkers(keyword = "", expectedSequence?: number) {
  const sequence = expectedSequence ?? ++reservationRequestSequence
  if (sequence !== reservationRequestSequence) return
  reservationLoading.value = true
  const selectedId = worker.value as number | undefined
  const selectedOption = selectedId == null
    ? undefined
    : workerList.value.find(item => item.value === selectedId)
  try {
    const page = await queryReservationEmployeeOptions({
      shopId,
      limit: RESERVATION_EMPLOYEE_PAGE_SIZE,
      selectedCorpUserIds: selectedId == null ? [] : [selectedId],
      ...(keyword ? { keyword } : {}),
    })
    if (sequence !== reservationRequestSequence) return
    const currentSelectedId = worker.value as number | undefined
    const currentSelectedOption = currentSelectedId == null
      ? undefined
      : workerList.value.find(item => item.value === currentSelectedId)
    reservationHasMore.value = Boolean(page.hasMore)
    workerList.value = mergeWorkerOptions(
      page.scope,
      currentSelectedOption ? [currentSelectedOption] : [],
      selectedOption ? [selectedOption] : [],
      page.selectedItems,
      page.defaultEmployee ? [page.defaultEmployee] : [],
      page.items,
    )
    if (worker.value == null) {
      const defaultId = page.defaultEmployee?.corpUserId
      worker.value = defaultId != null && workerList.value.some(item => item.value === defaultId)
        ? defaultId
        : workerList.value.find(item => item.value === userInfo?.corpUserId)?.value
    }
  } catch (error) {
    if (sequence !== reservationRequestSequence) return
    throw error
  } finally {
    if (sequence === reservationRequestSequence) reservationLoading.value = false
  }
}

function handleReservationRemoteSearch(value: string) {
  if (!usesReservationRemoteSearch.value) return
  const sequence = ++reservationRequestSequence
  if (reservationSearchTimer) clearTimeout(reservationSearchTimer)
  const keyword = value.trim()
  reservationSearchTimer = setTimeout(() => {
    reservationSearchTimer = undefined
    if (sequence !== reservationRequestSequence || !usesReservationRemoteSearch.value) return
    loadReservationWorkers(keyword, sequence).catch(async (error) => {
      if (sequence !== reservationRequestSequence || !usesReservationRemoteSearch.value) return
      if (shouldFallbackCandidate(error)) {
        await fallbackToLegacyWorkers()
        return
      }
      const message = error instanceof Error ? error.message : "预订员工搜索失败"
      ElMessage.error(message)
    })
  }, RESERVATION_EMPLOYEE_SEARCH_DELAY_MS)
}

function activateCompatibilityFallback() {
  compatibilityFallback.value = true
  reservationRequestSequence += 1
  reservationLoading.value = false
  reservationHasMore.value = false
  if (reservationSearchTimer) {
    clearTimeout(reservationSearchTimer)
    reservationSearchTimer = undefined
  }
}

function shouldFallbackCandidate(error: unknown) {
  const candidate = error as {
    code?: number
    status?: number
    response?: { status?: number }
  }
  const status = candidate?.status ?? candidate?.response?.status
  if (status != null) return status === 403 || status === 404 || status >= 500
  if (candidate?.code != null) return false
  return true
}

async function fallbackToLegacyWorkers() {
  activateCompatibilityFallback()
  ElMessage.warning("预订员工列表暂不可用，已切换为本店员工")
  try {
    await loadLegacyWorkers()
  } catch (fallbackError) {
    const message = fallbackError instanceof Error ? fallbackError.message : "本店员工列表加载失败"
    ElMessage.error(message)
  }
}

const getOptionWorkerList = async () => {
  if (props.fixedWorker) {
    worker.value = props.fixedWorker.value
    return
  }
  if (!usesReservationCandidateApi.value) {
    await loadLegacyWorkers()
    return
  }
  try {
    await loadReservationWorkers()
  } catch (error) {
    if (!shouldFallbackCandidate(error)) {
      const message = error instanceof Error ? error.message : "预订员工列表加载失败"
      ElMessage.error(message)
      return
    }
    await fallbackToLegacyWorkers()
  }
}

const handleClick = () => {
  if (props.selectionMode === "reservation" && !compatibilityFallback.value && worker.value == null) {
    ElMessage.warning("请选择预订员工")
    return
  }
  emit("update:worker", worker.value)
}

const handleSecondaryClick = () => {
  if (props.selectionMode === "reservation" && !compatibilityFallback.value && worker.value == null) {
    ElMessage.warning("请选择预订员工")
    return
  }
  emit("secondary-click", worker.value)
}

getOptionWorkerList().catch((error) => {
  const message = error instanceof Error ? error.message : "员工列表加载失败"
  ElMessage.error(message)
})

onBeforeUnmount(() => {
  reservationRequestSequence += 1
  if (reservationSearchTimer) clearTimeout(reservationSearchTimer)
})
</script>

<style scoped lang="scss">
.button {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 46px;
  padding: 12px 32px;
  margin-left: 32px;
  font-size: 20px;
  color: var(--tea-component-order-action-text-color);
  user-select: none;
  background: var(--tea-component-order-action-bg);
  border: 1px solid var(--tea-component-order-action-border-color);
  border-radius: 8px;
  box-shadow: var(--tea-component-order-action-shadow);
}

.button--after-secondary {
  margin-left: 12px;
}

.button--secondary {
  color: var(--tea-color-primary);
  background: var(--tea-color-bg-container, #fff);
  border-color: var(--tea-color-primary);
  box-shadow: none;
}

.worker-select__control {
  position: relative;
}

.worker-select__search-hint {
  position: absolute;
  top: 50px;
  left: 8px;
  z-index: 1;
  width: 220px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
</style>
